/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';
import { maxBy, sortBy } from 'lodash';
import PropTypes from 'prop-types';
import { branch, compose, getContext, renderComponent, withProps } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import deferToClientSideRender from 'js/lib/deferToClientSideRender';
import getModernContext from 'js/lib/getModernContext';
import user from 'js/lib/user';

import { Grid, PageGridContainer } from '@coursera/cds-core';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import GwGHelpBanner from 'bundles/enterprise-admin-grow-with-google/components/learner/GwGHelpBanner';
import AdminInvitationNotification from 'bundles/enterprise-admin-home/components/AdminInvitationNotification';
import AdminLandingExpired from 'bundles/enterprise-admin-home/components/AdminLandingExpired';
import EnterpriseAdminNotification from 'bundles/enterprise-admin-home/components/EnterpriseAdminNotification';
import LandingPageUploadLogo from 'bundles/enterprise-admin-home/components/LandingPageUploadLogo';
import LandingPageWelcome from 'bundles/enterprise-admin-home/components/LandingPageWelcome';
import LearningInsightsContainer from 'bundles/enterprise-admin-home/components/LearningInsightsContainer';
import ProgramSummaryListContainer from 'bundles/enterprise-admin-home/components/ProgramSummaryListContainer';
import ThirdPartyOrgUsageOverview from 'bundles/enterprise-admin-home/components/ThirdPartyOrgUsageOverview';
import type { SendMessageParams } from 'bundles/enterprise-admin-home/components/ThirdPartyOrgUsageOverview';
import { getProgramNameList, sortProgramsByTitle } from 'bundles/enterprise-admin-home/utils/ProgramSummaryUtils';
import type { ProgramNameList } from 'bundles/enterprise-admin-home/utils/ProgramSummaryUtils';
import type { ProgramOrder, ProgramSort } from 'bundles/enterprise-admin-home/utils/program-sort';
import { setCreateMessageParams } from 'bundles/enterprise-admin-messages/actions/CreateMessageParamsActions';
import type { CreateMessageParams } from 'bundles/enterprise-admin-messages/types';
import type { EnterpriseAdminPermissionsContextProps } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import { EnterpriseAdminPermissionsContext } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import { updateProgramsInLocalStorage } from 'bundles/enterprise-admin-skills/utils/programsLastVisited';
import type { Contract } from 'bundles/enterprise-admin/EnterpriseAdminContext';
import { useEnterpriseAdminContext } from 'bundles/enterprise-admin/EnterpriseAdminContext';
import { useThirdPartyOrganization } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';
import enterpriseAdminUser from 'bundles/enterprise-admin/enterprise-admin-index';
import { Spacer } from 'bundles/enterprise-ui/components';
import Naptime from 'bundles/naptimejs';
import type { EnterpriseOrganizationSummary } from 'bundles/naptimejs/resources/EnterpriseOrganizationSummaries.v1';
import EnterpriseProgramsV1 from 'bundles/naptimejs/resources/enterprisePrograms.v1';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

type PropsFromLegacyContext = {
  enableSkillsPlanningForOrg: boolean;
  enableGwGForOrg: boolean;
};

type PropsFromCaller = {};

type PropsFromHooks = {
  thirdPartyOrganization: {
    id: string;
    slug: string;
    name: string;
    rectangularLogo: string | null;
    squareLogo: string | null;
  };
  contracts: Contract[];
  hasSkillSets: boolean;
};

type PropsFromRouter = {
  router: InjectedRouter;
};

type PropsFromWithProps = {
  logo: string;
  hasContract: boolean;
  hasActiveContract: boolean;
  hasSelfServeContract: boolean;
  hasActiveProgram?: boolean;
  canManageOrganization: boolean;
  programNameList: ProgramNameList;
  isGwGOrg: boolean;
  enterprisePrograms?: Array<EnterpriseProgramsV1>;
};

type PropsFromOrganizationSummariesQuery = {
  thirdPartyOrgSummary?: EnterpriseOrganizationSummary;
  refetchThirdPartyOrgSummary?: (variables?: OrganizationSummariesQueryVariables) => Promise<any>;
};

type PropsFromNaptime = {
  allEnterprisePrograms?: Array<EnterpriseProgramsV1>;
};

export type Props = PropsFromCaller &
  PropsFromHooks &
  PropsFromRouter &
  PropsFromNaptime &
  PropsFromWithProps &
  PropsFromOrganizationSummariesQuery &
  PropsFromLegacyContext;

type State = {
  programSort: ProgramSort;
  programOrder: ProgramOrder;
};

const DeferredEnterpriseAdminNotification = deferToClientSideRender(EnterpriseAdminNotification);

export class AdminLandingPage extends React.Component<Props, State> {
  static contextTypes = {
    executeMutation: PropTypes.func.isRequired,
    executeAction: PropTypes.func.isRequired,
    getStore: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof AdminLandingPage.contextTypes>;

  constructor(props: Props) {
    super(props);
    this.state = {
      programSort: 'NAME',
      programOrder: 'ASC',
    };
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    const { enterprisePrograms: prevEnterprisePrograms } = prevProps;
    const { enterprisePrograms, thirdPartyOrganization } = this.props;

    // We need enterprisePrograms to have loaded, otherwise we will constantly overwrite the recently visited programs
    if (enterprisePrograms !== undefined && enterprisePrograms !== prevEnterprisePrograms) {
      // Update programs in local storage to keep LastVisitedProgramsList up to date
      // This filters out the archived programs
      updateProgramsInLocalStorage(enterprisePrograms, thirdPartyOrganization.id);
    }
  }

  componentDidMount = () => {
    const { router, thirdPartyOrganization, refetchThirdPartyOrgSummary } = this.props;
    const { getStore } = this.context;
    const { refetchAnalytics } = router.location.query;
    getStore('CreateMessageParamsStore').addChangeListener(this.handleCreateMessageParamsChange);
    if (refetchAnalytics && refetchThirdPartyOrgSummary) {
      refetchThirdPartyOrgSummary({ input: { organization_id: thirdPartyOrganization.id } });
    }
  };

  handleCreateMessageParamsChange = () => {
    const { router } = this.props;
    const { thirdPartySlug } = router.params;
    router.push(`/o/${thirdPartySlug}/admin/messages/new`);
  };

  sendMessage = ({ presetId, numberOfRecipients }: SendMessageParams) => {
    const { executeAction } = this.context;

    const params: CreateMessageParams = {
      presetId,
      filters: {
        programs: [],
        enrollments: [],
        contracts: [],
        businessUnits: [],
        jobTitles: [],
        locations: [],
        managers: [],
      },
      users: [],
      usersCount: numberOfRecipients,
      refetchUsersCount: true,
    };

    executeAction(setCreateMessageParams, { params });
  };

  render() {
    const {
      logo,
      hasActiveProgram,
      canManageOrganization,
      thirdPartyOrganization,
      thirdPartyOrgSummary,
      hasSelfServeContract,
      enterprisePrograms,
      contracts,
      programNameList,
      hasSkillSets,
      enableSkillsPlanningForOrg,
      isGwGOrg,
    } = this.props;

    const { programSort, programOrder } = this.state;
    const hasAccessToAnalytics = canManageOrganization;

    const orgHasSkillSets = enableSkillsPlanningForOrg || hasSkillSets;

    return (
      <TrackedDiv
        role="main"
        data-e2e="Admin_Landing_Page"
        trackingName="home_page"
        withVisibilityTracking={true}
        requireFullyVisible={false}
        trackClicks={false}
      >
        <div style={{ boxShadow: '0 2px 4px 0 var(--cds-color-darken-100)' }}>
          <PageGridContainer maxWidth="lg">
            <Grid item xs={12}>
              {isGwGOrg && <GwGHelpBanner isAuthenticatedUser={user.isAuthenticatedUser()} isAdminFacing={true} />}
              <AdminInvitationNotification />
              <DeferredEnterpriseAdminNotification
                thirdPartyOrganization={thirdPartyOrganization}
                thirdPartyOrgSummary={thirdPartyOrgSummary}
                hasActiveProgram={hasActiveProgram}
              />
              {/* No Active Program */}
              {hasActiveProgram === false && (
                <LandingPageWelcome
                  logo={logo}
                  canManageOrganization={canManageOrganization}
                  thirdPartyOrganization={thirdPartyOrganization}
                />
              )}
            </Grid>
          </PageGridContainer>

          {/* Active Program and Redesign enabled */}
          {(hasActiveProgram === true || hasActiveProgram === undefined) && (
            <div>
              <LearningInsightsContainer
                programNameList={programNameList}
                orgId={thirdPartyOrganization.id}
                orgSlug={thirdPartyOrganization.slug}
              />
              <PageGridContainer maxWidth="lg">
                <Grid item xs={12}>
                  <ThirdPartyOrgUsageOverview
                    usageSummary={thirdPartyOrgSummary}
                    programNameList={programNameList}
                    thirdPartyOrganization={thirdPartyOrganization}
                    sendMessage={this.sendMessage}
                    hasAccessToAnalytics={hasAccessToAnalytics}
                    enableSkillsPlanning={orgHasSkillSets}
                    contracts={contracts}
                    className="p-t-2"
                  />
                </Grid>
              </PageGridContainer>
            </div>
          )}
        </div>
        <PageGridContainer maxWidth="lg">
          <Grid item xs={12}>
            <Spacer size={48} />
            <ProgramSummaryListContainer
              hasActiveProgram={hasActiveProgram}
              enterprisePrograms={enterprisePrograms}
              hasSelfServeContract={hasSelfServeContract}
              canManageOrganization={canManageOrganization}
              thirdPartyOrganization={thirdPartyOrganization}
              programSort={programSort}
              programOrder={programOrder}
              onProgramSortChange={(sort) => this.setState({ programSort: sort })}
              onProgramOrderChange={(order) => this.setState({ programOrder: order })}
              contracts={contracts}
            />

            {canManageOrganization && hasActiveProgram && !logo && (
              <div style={{ height: '9.4rem' }}>
                <LandingPageUploadLogo orgName={thirdPartyOrganization.name} />
              </div>
            )}
          </Grid>
        </PageGridContainer>
      </TrackedDiv>
    );
  }
}

const branchExpiredLandingPage = (Component: React.ComponentType) =>
  branch(
    (props: PropsFromWithProps) => props.hasContract && props.hasSelfServeContract && !props.hasActiveContract,
    compose(
      withProps<
        {
          expiredContract?: { endedAt?: number };
          thirdPartyOrganizationId: string;
        },
        PropsFromCaller & PropsFromHooks & PropsFromNaptime
      >(({ contracts, thirdPartyOrganization }) => ({
        expiredContract: maxBy(contracts, (contract) => contract.endedAt),
        thirdPartyOrganizationId: thirdPartyOrganization.id,
      })),
      renderComponent(AdminLandingExpired)
    )
  )(Component);

type OrganizationSummariesQueryVariables = {
  input: {
    organization_id: string;
  };
};

type OrganizationSummariesQueryResult = {
  OrganizationSummariesResponse?: {
    enterpriseOrganizationSummary?: EnterpriseOrganizationSummary;
  };
};

/* eslint-disable graphql/template-strings */
export const OrganizationSummariesQuery = gql`
  query OrganizationSummariesQuery($input: OrganizationSummariesQueryVariables!) {
    OrganizationSummariesResponse(input: $input)
      @rest(
        type: "OrganizationSummariesQueryResult"
        path: "grpc/enterprisesummaries/enterpriseorganizationsummaries/v1/EnterpriseOrganizationSummariesAPI/GetEnterpriseOrganizationSummaryById"
        method: "POST"
      ) {
      enterpriseOrganizationSummary
    }
  }
`;

type PropsFromEnterprisePermissionsContext = {
  enterpriseAdminPermissionsContext: EnterpriseAdminPermissionsContextProps;
};

const fetchNewSummaryDataGRPC = () =>
  graphql<
    PropsFromCaller & PropsFromHooks,
    OrganizationSummariesQueryResult,
    OrganizationSummariesQueryVariables,
    PropsFromOrganizationSummariesQuery
  >(OrganizationSummariesQuery, {
    options: ({ thirdPartyOrganization }) => ({
      variables: {
        input: {
          // eslint-disable-next-line camelcase
          organization_id: thirdPartyOrganization.id,
        },
      },
      errorPolicy: 'all',
      ssr: false,
    }),
    props: ({ data }) => {
      const thirdPartyOrgSummary = data?.OrganizationSummariesResponse?.enterpriseOrganizationSummary;
      return { thirdPartyOrgSummary, refetchThirdPartyOrgSummary: data?.refetch };
    },
  });

const enhanced = compose<Props, PropsFromCaller & PropsFromHooks>(
  connectToRouter((router) => ({ router })),
  fetchNewSummaryDataGRPC(),

  Naptime.createContainer<PropsFromNaptime, PropsFromCaller & PropsFromHooks>(({ thirdPartyOrganization }) => ({
    allEnterprisePrograms: EnterpriseProgramsV1.finder('byThirdPartyOrganizationId', {
      params: { thirdPartyOrganizationId: thirdPartyOrganization.id, includeArchived: true },
      fields: ['metadata', 'contractId', 'launchedAt'],
      required: false,
    }),
  })),
  getContext<PropsFromLegacyContext>({
    enableSkillsPlanningForOrg: PropTypes.bool,
    enableGwGForOrg: PropTypes.bool,
  }),
  getModernContext('enterpriseAdminPermissionsContext', EnterpriseAdminPermissionsContext),

  withProps<
    PropsFromWithProps,
    PropsFromCaller & PropsFromHooks & PropsFromNaptime & PropsFromLegacyContext & PropsFromEnterprisePermissionsContext
  >((props) => {
    const {
      allEnterprisePrograms,
      contracts,
      thirdPartyOrganization,
      enableGwGForOrg,
      enterpriseAdminPermissionsContext,
    } = props;
    const { rectangularLogo, squareLogo } = thirdPartyOrganization;
    //
    // could be done in parent and used for the gradebook export as well.
    //
    // note: even though the types require enterprisePrograms, some tests, which
    // are still javascript, do not provide this value.
    //
    const sortedPrograms = sortProgramsByTitle(allEnterprisePrograms ?? []);
    const programNameList = sortBy(getProgramNameList(sortedPrograms), (program) => program.isArchived);

    const isGwGOrg = enableGwGForOrg && enterpriseAdminUser.isGwGOrg;

    const enterprisePrograms = allEnterprisePrograms?.filter((p) => !p.metadata.endedAt);

    return {
      logo: rectangularLogo || squareLogo || '',
      hasContract: contracts.length > 0,
      hasActiveContract: contracts.some((c) => !c.endedAt),
      hasSelfServeContract: contracts.some((c) => c.contractType === 'SELF_SERVE'),
      hasActiveProgram: enterprisePrograms && enterprisePrograms.length > 0,
      canManageOrganization: enterpriseAdminPermissionsContext.getIsOrganizationDomainAdmin(),
      enterprisePrograms,
      programNameList,
      isGwGOrg,
    };
  }),
  branchExpiredLandingPage
);

const EnhancedAdminLandingPage = enhanced(AdminLandingPage);

const AdminLandingPageWithHooks: React.FC<{}> = (props) => {
  const thirdPartyOrganization = useThirdPartyOrganization();
  const { hasSkillSets, contracts } = useEnterpriseAdminContext();

  return (
    <ErrorBoundaryWithTagging componentName="AdminLandingPage">
      <EnhancedAdminLandingPage
        {...props}
        thirdPartyOrganization={thirdPartyOrganization}
        hasSkillSets={hasSkillSets ?? false}
        contracts={contracts ?? []}
      />
    </ErrorBoundaryWithTagging>
  );
};

export default AdminLandingPageWithHooks;
